import {Form, FormInstance} from 'antd';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {SelectInfiniteScroll} from '../../../../../../component/select-infinite-scroll/select-infinite-scroll';
import {Locale} from '../../../../../../provider/locale/localization';
import {useDebouncedValue} from '../../../../../../service/debounce-hook/debounce-hook';
import {
    useCompetitorCompanies,
    useCompetitorCompanyInfo,
} from '../../../../../../service/reivew-analysis/competitors/competitiors-api';
import {defaultCompaniesCountPerPage} from '../../../../../../service/reivew-analysis/competitors/competitors-const';
import {CompetitorCompanyType} from '../../../../../../service/reivew-analysis/competitors/competitors-type';
import {renderSelectOption} from '../../../../photo-tool/pages/photo-tool-list/photo-tool-controls/photo-filter/photo-filter-form/photo-filter-form-helper';
import {ReviewsAnalysisFilterEnum} from '../../../reviews-analysis-type';

import * as styles from './competitor-companies-select.scss';

type PropsType = {
    form: FormInstance;
    mainFilterKey: string;
};

export function CompetitorCompaniesSelect({form, mainFilterKey}: PropsType): JSX.Element {
    const [searchText, setSearchText] = useState<string>('');

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const competitorBrandId = Form.useWatch([ReviewsAnalysisFilterEnum.Competitor], {
        form,
        preserve: true,
    });

    const [competitorCompanies, setCompetitorCompanies] = useState<Array<CompetitorCompanyType>>([]);

    const debouncedSearchText = useDebouncedValue(searchText, '', 400);

    const {data, isLoading, pagination} = useCompetitorCompanies({
        brandId: competitorBrandId,
        filterKey: mainFilterKey,
        q: debouncedSearchText,
    });

    const {data: competitorCompanyInfo, isFetching: isCompetitorCompanyInfoLoading} = useCompetitorCompanyInfo(
        form.getFieldValue([ReviewsAnalysisFilterEnum.CompetitorCompany])
    );

    const onChange = useCallback((companyId: number | null) => {
        form.setFieldValue([ReviewsAnalysisFilterEnum.CompetitorCompany], companyId);
    }, []);

    useEffect(() => {
        setCompetitorCompanies([]);
        form.setFieldValue([ReviewsAnalysisFilterEnum.CompetitorCompany], null);
    }, [mainFilterKey, competitorBrandId]);

    useEffect(() => {
        if (data) {
            setCompetitorCompanies((previousCompanies) => {
                const newResults = data.results.filter(
                    (newCompany) => !previousCompanies.some((existing) => existing.id === newCompany.id)
                );

                return [...previousCompanies, ...newResults];
            });
        }
    }, [data]);

    useEffect(() => {
        if (competitorCompanyInfo && !competitorCompanies.some((company) => company.id === competitorCompanyInfo.id)) {
            setCompetitorCompanies((previousCompanies) => [competitorCompanyInfo, ...previousCompanies]);
        }
    }, [competitorCompanyInfo, competitorCompanies]);

    const {onChange: onPaginationChange, page, total} = pagination;

    const onSearch = useCallback((newSearchText: string) => {
        setSearchText(newSearchText);
        setCompetitorCompanies([]);
    }, []);

    const onLoadMore = useCallback(() => {
        if (!isLoading && isOpen && page * defaultCompaniesCountPerPage < total) {
            onPaginationChange(page + 1);
        }
    }, [page, isLoading, isOpen, total]);

    const selectOptions = useMemo(() => {
        return competitorCompanies.map((competitorCompany) => ({
            id: competitorCompany.id,
            name: `${competitorCompany.name}, ${competitorCompany.address}`,
        }));
    }, [competitorCompanies]);

    return (
        <Form.Item name={ReviewsAnalysisFilterEnum.CompetitorCompany}>
            <SelectInfiniteScroll<number>
                allowClear
                className={styles.CompetitorCompaniesSelect}
                filterOption={() => true}
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                loading={isLoading || isCompetitorCompanyInfoLoading}
                onChange={onChange}
                onDropdownVisibleChange={setIsOpen}
                onLoadMore={onLoadMore}
                onSearch={onSearch}
                placeholder={<Locale stringKey="REVIEWS_ANALYSIS__FORM__INPUT__COMPETITOR_COMPANY__PLACEHOLDER" />}
                searchValue={searchText}
                showSearch
            >
                {selectOptions.map(renderSelectOption)}
            </SelectInfiniteScroll>
        </Form.Item>
    );
}
